<template>
  <div class="column content-box canned-management-wrapper">
    <div v-if="!canCreateShortcut" class="permission-overlay">
      <div class="permission-overlay-content">
        <i class="icon ion-lock-closed" />

        <h3>Sem permissão</h3>

        <p>
          Você não possui permissão para criar, editar ou remover atalhos.
          Solicite acesso a um administrador.
        </p>
      </div>
    </div>

    <div :class="{ 'blocked-content': !canCreateShortcut }">
      <woot-button
        color-scheme="success"
        class-names="button--fixed-top"
        icon="add-circle"
        :disabled="!canCreateShortcut"
        @click="openAddPopup()"
      >
        {{ $t('CANNED_MGMT.HEADER_BTN_TXT') }}
      </woot-button>

      <div class="row">
        <div class="small-8 columns with-right-space">
          <p
            v-if="!uiFlags.fetchingList && !records.length"
            class="no-items-error-message"
          >
            {{ $t('CANNED_MGMT.LIST.404') }}
          </p>

          <woot-loading-state
            v-if="uiFlags.fetchingList"
            :message="$t('CANNED_MGMT.LOADING')"
          />

          <table
            v-if="!uiFlags.fetchingList && records.length"
            class="woot-table"
          >
            <thead>
              <th
                v-for="thHeader in $t('CANNED_MGMT.LIST.TABLE_HEADER')"
                :key="thHeader"
              >
                {{ thHeader }}
              </th>
            </thead>

            <tbody>
              <tr
                v-for="(cannedItem, index) in records"
                :key="cannedItem.short_code"
              >
                <td class="short-code">
                  {{ cannedItem.short_code }}
                </td>

                <td class="wrap-break-words">
                  {{ cannedItem.content }}
                </td>

                <td class="button-wrapper">
                  <woot-button
                    v-tooltip.top="$t('CANNED_MGMT.EDIT.BUTTON_TEXT')"
                    variant="smooth"
                    size="tiny"
                    color-scheme="secondary"
                    icon="edit"
                    :disabled="!canCreateShortcut"
                    @click="openEditPopup(cannedItem)"
                  />

                  <woot-button
                    v-tooltip.top="$t('CANNED_MGMT.DELETE.BUTTON_TEXT')"
                    variant="smooth"
                    color-scheme="alert"
                    size="tiny"
                    icon="dismiss-circle"
                    class-names="grey-btn"
                    :disabled="!canCreateShortcut"
                    :is-loading="loading[cannedItem.id]"
                    @click="openDeletePopup(cannedItem, index)"
                  />
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div class="small-4 columns">
          <span v-dompurify-html="$t('CANNED_MGMT.SIDEBAR_TXT')" />
        </div>
      </div>
    </div>

    <woot-modal :show.sync="showAddPopup" :on-close="hideAddPopup">
      <add-canned :on-close="hideAddPopup" />
    </woot-modal>

    <woot-modal :show.sync="showEditPopup" :on-close="hideEditPopup">
      <edit-canned
        v-if="showEditPopup"
        :id="selectedResponse.id"
        :edshort-code="selectedResponse.short_code"
        :edcontent="selectedResponse.content"
        :edinboxes="selectedResponse.inbox_ids"
        :on-close="hideEditPopup"
      />
    </woot-modal>

    <woot-delete-modal
      :show.sync="showDeleteConfirmationPopup"
      :on-close="closeDeletePopup"
      :on-confirm="confirmDeletion"
      :title="$t('CANNED_MGMT.DELETE.CONFIRM.TITLE')"
      :message="$t('CANNED_MGMT.DELETE.CONFIRM.MESSAGE')"
      :message-value="deleteMessage"
      :confirm-text="deleteConfirmText"
      :reject-text="deleteRejectText"
    />
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import AddCanned from './AddCanned';
import EditCanned from './EditCanned';
import adminMixin from 'dashboard/mixins/isAdmin';
import agentMixin from 'dashboard/mixins/agentMixin';

export default {
  components: {
    AddCanned,
    EditCanned,
  },
  mixins: [adminMixin, agentMixin],
  data() {
    return {
      loading: {},
      showAddPopup: false,
      showEditPopup: false,
      showDeleteConfirmationPopup: false,
      selectedResponse: {},
      cannedResponseAPI: {
        message: '',
      },
      agentsLoaded: false,
    };
  },
  computed: {
    ...mapGetters({
      records: 'getCannedResponses',
      uiFlags: 'getUIFlags',
      currentUser: 'getCurrentUser',
      currentRole: 'getCurrentRole',
    }),
    canCreateShortcut() {
      if (!this.agentsLoaded) {
        return true;
      }

      const canShortcut = this.agentRules?.create_shortcut !== false;

      return (
        canShortcut ||
        this.currentUser?.type === 'SuperAdmin' ||
        this.currentRole === 'administrator' ||
        this.currentRole === 'team_supervisor'
      );
    },
    deleteConfirmText() {
      return `${this.$t('CANNED_MGMT.DELETE.CONFIRM.YES')} ${
        this.selectedResponse.short_code
      }`;
    },
    deleteRejectText() {
      return `${this.$t('CANNED_MGMT.DELETE.CONFIRM.NO')} ${
        this.selectedResponse.short_code
      }`;
    },
    deleteMessage() {
      return ` ${this.selectedResponse.short_code}?`;
    },
  },
  async mounted() {
    await this.$store.dispatch('agents/get');
    this.agentsLoaded = true;

    this.$store.dispatch('getCannedResponse');
  },
  methods: {
    showAlert(message) {
      this.loading[this.selectedResponse.id] = false;
      this.selectedResponse = {};
      this.cannedResponseAPI.message = message;

      bus.$emit('newToastMessage', message);
    },
    openAddPopup() {
      if (!this.canCreateShortcut) {
        this.showAlert('Você não possui permissão para criar atalhos.');
        return;
      }

      this.showAddPopup = true;
    },
    hideAddPopup() {
      this.showAddPopup = false;
    },
    openEditPopup(response) {
      if (!this.canCreateShortcut) {
        this.showAlert('Você não possui permissão para editar atalhos.');
        return;
      }

      this.showEditPopup = true;
      this.selectedResponse = response;
    },
    hideEditPopup() {
      this.showEditPopup = false;
    },
    openDeletePopup(response) {
      if (!this.canCreateShortcut) {
        this.showAlert('Você não possui permissão para remover atalhos.');
        return;
      }

      this.showDeleteConfirmationPopup = true;
      this.selectedResponse = response;
    },
    closeDeletePopup() {
      this.showDeleteConfirmationPopup = false;
    },
    confirmDeletion() {
      if (!this.canCreateShortcut) {
        this.showAlert('Você não possui permissão para remover atalhos.');
        return;
      }

      this.loading[this.selectedResponse.id] = true;

      this.closeDeletePopup();

      this.deleteCannedResponse(this.selectedResponse.id);
    },
    deleteCannedResponse(id) {
      if (!this.canCreateShortcut) {
        this.showAlert('Você não possui permissão para remover atalhos.');
        return;
      }

      this.$store
        .dispatch('deleteCannedResponse', id)
        .then(() => {
          this.showAlert(this.$t('CANNED_MGMT.DELETE.API.SUCCESS_MESSAGE'));
        })
        .catch(error => {
          const errorMessage =
            error?.message || this.$t('CANNED_MGMT.DELETE.API.ERROR_MESSAGE');

          this.showAlert(errorMessage);
        });
    },
  },
};
</script>

<style scoped>
.canned-management-wrapper {
  position: relative;
}

.permission-overlay {
  position: absolute;
  inset: 0;
  z-index: 999;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(17, 24, 39, 0.72);
  backdrop-filter: blur(4px);
  border-radius: 12px;
}

.permission-overlay-content {
  max-width: 720px;
  width: 90%;
  padding: 48px;
  text-align: center;
  background: #ffffff;
  border-radius: 20px;
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.28);
}

.permission-overlay-content i {
  font-size: 88px;
  margin-bottom: 24px;
  display: block;
}

.permission-overlay-content h3 {
  margin-bottom: 24px;
  font-size: 42px;
  font-weight: 800;
  line-height: 1.2;
}

.permission-overlay-content p {
  margin: 0;
  font-size: 24px;
  line-height: 1.8;
  font-weight: 500;
}

.blocked-content {
  pointer-events: none;
  user-select: none;
  filter: grayscale(0.5);
}

.short-code {
  width: 14rem;
}

.wrap-break-words {
  word-break: break-all;
  white-space: normal;
}
</style>
